import React, { useTransition, useState, useEffect, useCallback } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';

import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  IconButton,
  List,
  Button,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Divider,
  Collapse,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import { useAuth } from '../../hooks';
import { Products } from './Products';
import { useAppContext } from '../../hooks';
import { useCheckPermissions } from '../../shared';
import { Dropdown, PermissionCheck } from '../../components';
import { bottomMenuLabels, MenuLabel } from './const';
import { PERMISSIONS } from '../../shared/constants/permissions';

const AGENTS_BASE_URL = 'agents';

const Jessica = React.lazy(() => import('../Jessica'));

const drawerWidth = 240;

export type MenuItem = {
  url?: string;
  isPublic?: boolean;

  menuTitle?: string;
  pageTitle?: string;

  onClick?: () => void;
  icon?: React.ReactNode;
  component?: React.ComponentType<any>;
  action?: SpPermission | SpPermission[];
};

type ResponsiveLayoutProps = {
  menuItems: MenuItem[];
  children: React.ReactNode;
};

const MenuItemComponent: React.FC<{
  menuItem: MenuItem;
  organization_id?: string;
}> = ({ menuItem: item, organization_id }) => {
  const theme = useTheme();

  const element = (
    <ListItem
      disablePadding
      sx={{
        color:
          item.menuTitle === MenuLabel.Logout
            ? theme.palette.error.main
            : theme.palette.text.primary,
        '&:hover': {
          color:
            item.menuTitle === MenuLabel.Logout
              ? theme.palette.error.dark
              : theme.palette.primary.main,
          backgroundColor:
            item.menuTitle === MenuLabel.Logout
              ? 'transparent'
              : theme.palette.primary.light,
          transition: 'all 0.3s ease',
        },
      }}
    >
      {item.url ? (
        <NavLink
          to={
            item.url.includes('/:organization_id')
              ? `/${AGENTS_BASE_URL}/${organization_id}`
              : item.url
          }
          exact={item.url === '/'}
          style={isActive => ({
            textDecoration: 'none',
            color: isActive ? theme.palette.primary.main : undefined,
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            padding: '4px 8px',
            fontWeight: isActive ? 500 : 400,
          })}
        >
          {item.icon && (
            <ListItemIcon
              sx={{
                fontSize: '20px',
                minWidth: '35px',
                '& svg': { fontSize: '20px' },
              }}
            >
              {item.icon}
            </ListItemIcon>
          )}
          <ListItemText
            style={{ fontWeight: 'inherit' }}
            sx={{
              '& .MuiTypography-root': {
                fontSize: '14px',
                fontWeight: 'inherit',
              },
            }}
            primary={item.menuTitle}
          />
        </NavLink>
      ) : (
        <Button
          onClick={item.onClick}
          fullWidth
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            padding: '8px 16px',
            color:
              item.menuTitle === MenuLabel.Logout
                ? theme.palette.error.main
                : theme.palette.text.primary,
            '&:hover': {
              color:
                item.menuTitle === MenuLabel.Logout
                  ? theme.palette.error.dark
                  : theme.palette.primary.main,
              backgroundColor:
                item.menuTitle === MenuLabel.Logout
                  ? 'transparent'
                  : theme.palette.primary.light,
            },
          }}
        >
          {item.icon && (
            <ListItemIcon
              sx={{ fontSize: '20px', '& svg': { fontSize: '20px' } }}
            >
              {item.icon}
            </ListItemIcon>
          )}
          <ListItemText
            primary={item.menuTitle}
            sx={{ '& .MuiTypography-root': { fontSize: '16px' } }}
          />
        </Button>
      )}
    </ListItem>
  );

  return item.action ? (
    <PermissionCheck action={item.action}>{element}</PermissionCheck>
  ) : (
    element
  );
};

const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({
  menuItems,
  children,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();

  const [isPending, startTransition] = useTransition();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showOrgConfig, setShowOrgConfig] = useState(false);
  const {
    user,
    orgs,
    isAuthorized,
    organization_id: selectedOrg,
    setSelectedOrg,
  } = useAuth();
  const { title } = useAppContext();

  const redirectToParent = () => {
    const currentPath = history?.location?.pathname || '/';
    const pathSegments = currentPath.split('/').filter(Boolean);

    if (pathSegments.length >= 2) {
      const basePath = `/${pathSegments[0]}`;
      history.push(basePath);
    } else {
      history.push(currentPath);
    }
  };

  const handleOrganizationChange = (newOrg: string) => {
    startTransition(() => {
      setSelectedOrg(newOrg);
      if (location.pathname.startsWith(`/${AGENTS_BASE_URL}`)) {
        history.replace(`/${AGENTS_BASE_URL}/${newOrg}`);
      } else {
        redirectToParent();
      }
    });
  };

  const handleOrganizationPath = useCallback(() => {
    if (location.pathname.startsWith(`/${AGENTS_BASE_URL}`)) {
      const agentIdMatch = location.pathname.match(
        new RegExp(`/${AGENTS_BASE_URL}/([^/]+)/([^/]+)`)
      );
      const orgIdMatch = location.pathname.match(
        new RegExp(`/${AGENTS_BASE_URL}/([^/]+)`)
      );

      const orgIdFromUrl = agentIdMatch
        ? agentIdMatch[1]
        : orgIdMatch
          ? orgIdMatch[1]
          : null;

      if (orgIdFromUrl) {
        const orgExists = orgs.some(org => org.id === orgIdFromUrl);
        if (orgExists && orgIdFromUrl !== selectedOrg) {
          startTransition(() => setSelectedOrg(orgIdFromUrl));
        }
      }
    }
  }, [location, selectedOrg, orgs, setSelectedOrg]);

  useEffect(() => {
    handleOrganizationPath();
  }, [handleOrganizationPath]);

  const permissions = useCheckPermissions(
    menuItems
      .map(item => item.action)
      .filter((action): action is SpPermission | SpPermission[] =>
        Boolean(action)
      )
      .flat()
  );

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const topMenuItems: MenuItem[] = [];
  const bottomMenuItems: MenuItem[] = [];
  let logoutItem: MenuItem | undefined = undefined;

  menuItems.forEach(item => {
    const hasAccess =
      !item.action ||
      (Array.isArray(item.action)
        ? item.action.some(action =>
            permissions.some(p => p.allowed && p.action === action)
          )
        : permissions.some(p => p.allowed && p.action === item.action));
    if (!hasAccess || !item.menuTitle) return;
    if (item.menuTitle === MenuLabel.Logout) {
      logoutItem = item;
      return;
    }
    const isBottomItem = bottomMenuLabels.includes(item.menuTitle);
    if (isBottomItem) bottomMenuItems.push(item);
    else topMenuItems.push(item);
  });

  const drawer = (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      sx={{ overflow: 'hidden' }}
    >
      <Toolbar />
      <Box
        flex="1 1 auto"
        display="flex"
        flexDirection="column"
        overflow="auto"
        px={1}
        mt="2.5rem"
      >
        {!topMenuItems.length ? (
          <div className="pl-md text-medium font-semibold">
            You have no access, please contact your administrator
          </div>
        ) : (
          <>
            <List>
              {topMenuItems.map((item, index) => (
                <MenuItemComponent
                  key={index}
                  menuItem={item}
                  organization_id={selectedOrg}
                />
              ))}
              <PermissionCheck action={PERMISSIONS.SUPER_ADMIN}>
                <ListItem
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setShowOrgConfig(prev => !prev)}
                >
                  <ListItemText
                    sx={{
                      '& .MuiTypography-root': {
                        fontSize: '14px',
                        color: theme.palette.text.secondary,
                      },
                    }}
                    primary="Platform config"
                  />
                  {showOrgConfig ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
              </PermissionCheck>
              <Collapse in={showOrgConfig} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {bottomMenuItems.map((item, index) => (
                    <MenuItemComponent key={index} menuItem={item} />
                  ))}
                </List>
              </Collapse>
            </List>
            <Products />
          </>
        )}
      </Box>
      {!!logoutItem && (
        <>
          <Divider sx={{ my: 2 }} />
          <List>
            <MenuItemComponent menuItem={logoutItem} />
          </List>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {isAuthorized && <Jessica />}
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h4"
            noWrap
            component="div"
            id="dynamic-page-title"
            className="font-medium text-main"
          >
            {title}
          </Typography>
          <div id="jessica-mount" className="ml-md" />
          <Dropdown
            value={selectedOrg}
            disabled={isPending}
            className="max-w-[20rem] ml-auto"
            placeholder="Select organization"
            options={orgs.map(o => ({
              value: o.id || '',
              label: o.name || '',
            }))}
            onChange={handleOrganizationChange}
          />
          <div id="dynamic-top-panel" className="ml-md" />
          {!!user?.name && (
            <Typography
              variant="h5"
              noWrap
              component="div"
              className="ml-md font-medium"
            >
              {user?.name}
            </Typography>
          )}
          {user?.picture && (
            <img
              alt=""
              src={user?.picture}
              className="rounded-full size-16 ml-sm"
            />
          )}
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{
          width: { sm: drawerWidth },
          flexShrink: { sm: 0 },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            borderRight: `1px solid ${theme.palette.divider}`,
          },
        }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', sm: 'none' } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{ display: { xs: 'none', sm: 'block' } }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          height: '100vh',
          p: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default ResponsiveLayout;
